import React from "react";

/**
 * Interface for the TriangleIcon component props.
 * @interface TriangleIconProps
 * @property {('up'|'down')} direction - Determines whether the triangle points up or down
 * @property {string} [className] - Optional CSS class name for styling the SVG
 * @property {string} [color] - Optional color for the triangle (default: 'currentColor')
 */
interface TriangleIconProps {
  direction: "up" | "down";
  className?: string;
  color?: string;
}

/**
 * A customizable triangle icon component that can point up or down.
 * Used for dropdown indicators, sorting icons, or other directional UI elements.
 *
 * @component
 * @example
 * // Triangle pointing up with default color
 * <TriangleIcon direction="up" />
 *
 * @example
 * // Triangle pointing down with custom color and class
 * <TriangleIcon
 *   direction="down"
 *   color="#FF0000"
 *   className="sort-indicator"
 * />
 *
 * @param {TriangleIconProps} props - Component props
 * @param {('up'|'down')} props.direction - Direction the triangle should point
 * @param {string} [props.className=''] - CSS class name for additional styling
 * @param {string} [props.color='currentColor'] - Fill color of the triangle
 * @returns {JSX.Element} SVG triangle icon
 */
const TriangleIcon: React.FC<TriangleIconProps> = ({
  direction,
  className = "",
  color = "currentColor",
}) => {
  const rotation = direction === "up" ? 0 : 180;
  return (
    <svg
      viewBox="0 0 256 256"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{
        transform: `rotate(${rotation}deg)`,
        width: "0.8rem",
        height: "0.8rem",
        display: "inline-block",
        verticalAlign: "middle",
      }}
    >
      <rect fill="none" height="256" width="256" />
      <path
        d="M236.7,188L148.8,36a24,24,0,0,0-41.6,0h0L19.3,188A23.9,23.9,0,0,0,40,224H216a23.9,23.9,0,0,0,20.7-36Z"
        fill={color}
      />
    </svg>
  );
};

export default TriangleIcon;
